import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, PencilIcon, TrashIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import type { Department, MyTimesheetEntry } from '../../types/timesheets';

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function monthStartISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

interface FormState {
  departmentId: string;
  workDate: string;
  hours: string;
  notes: string;
}

function EntryModal({
  entry,
  departments,
  onClose,
}: {
  entry: MyTimesheetEntry | null;
  departments: Department[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(
    entry
      ? { departmentId: entry.department_id, workDate: entry.work_date, hours: String(entry.hours), notes: entry.notes ?? '' }
      : { departmentId: departments[0]?.id ?? '', workDate: todayISO(), hours: '', notes: '' },
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const hours = Number(form.hours);
      const payload = { departmentId: form.departmentId, workDate: form.workDate, hours, notes: form.notes || null };
      return entry ? api.updateTimesheetEntry(entry.id, payload) : api.createTimesheetEntry(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-timesheet'] });
      onClose();
    },
    onError: (e: any) => setError(e?.response?.data?.error ?? 'Could not save entry'),
  });

  const hoursNum = Number(form.hours);
  const valid = form.departmentId && form.workDate && hoursNum > 0 && hoursNum <= 24;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-xl w-full max-w-md">
        <div className="p-5 border-b border-slate-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">{entry ? 'Edit Entry' : 'Log Hours'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <XCircleIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Department *</label>
            <select
              value={form.departmentId}
              onChange={(e) => setForm((f) => ({ ...f, departmentId: e.target.value }))}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
            >
              <option value="" disabled>Select a department…</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Date *</label>
              <input
                type="date"
                value={form.workDate}
                onChange={(e) => setForm((f) => ({ ...f, workDate: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Hours *</label>
              <input
                type="number"
                min="0.25"
                max="24"
                step="0.25"
                value={form.hours}
                onChange={(e) => setForm((f) => ({ ...f, hours: e.target.value }))}
                placeholder="3.5"
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-slate-400 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={2}
              placeholder="What did you work on?"
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none resize-none"
            />
          </div>

          {error && <p className="text-sm text-red-400">{error}</p>}
        </div>

        <div className="p-5 border-t border-slate-700 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">
            Cancel
          </button>
          <button
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
            className="px-4 py-2 text-sm bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg disabled:opacity-50 transition-colors"
          >
            {save.isPending ? 'Saving…' : entry ? 'Save Changes' : 'Log Hours'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function MyTimesheetPage() {
  const qc = useQueryClient();
  const [from, setFrom] = useState(monthStartISO());
  const [to, setTo] = useState(todayISO());
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<MyTimesheetEntry | null>(null);

  const { data: departments = [] } = useQuery({
    queryKey: ['departments', 'active'],
    queryFn: () => api.listDepartments(false),
  });

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ['my-timesheet', { from, to }],
    queryFn: () => api.listMyTimesheetEntries({ from, to }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteTimesheetEntry(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-timesheet'] }),
  });

  const totalHours = entries.reduce((sum, e) => sum + Number(e.hours), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">My Timesheet</h1>
          <p className="text-slate-400 mt-1">Log the hours you work against each department.</p>
        </div>
        <button
          onClick={() => { setEditTarget(null); setShowModal(true); }}
          disabled={departments.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
        >
          <PlusIcon className="w-4 h-4" />
          Log Hours
        </button>
      </div>

      {/* Period filter + total */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs text-slate-400 mb-1">From</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
            className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none" />
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">To</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
            className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none" />
        </div>
        <div className="ml-auto flex items-center gap-2 text-slate-300">
          <ClockIcon className="w-5 h-5 text-cyan-400" />
          <span className="text-sm">Total this period:</span>
          <span className="text-lg font-semibold text-white">{totalHours.toFixed(2)} h</span>
        </div>
      </div>

      {isLoading ? (
        <div className="text-slate-400 text-sm">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-12 text-center">
          <ClockIcon className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">No hours logged for this period yet.</p>
        </div>
      ) : (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left px-4 py-3 text-slate-400 font-medium">Date</th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium">Department</th>
                <th className="text-right px-4 py-3 text-slate-400 font-medium">Hours</th>
                <th className="text-left px-4 py-3 text-slate-400 font-medium">Notes</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50">
              {entries.map((e) => (
                <tr key={e.id} className="hover:bg-slate-700/20 transition-colors">
                  <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{e.work_date}</td>
                  <td className="px-4 py-3 text-white">{e.department_name}</td>
                  <td className="px-4 py-3 text-right text-slate-300">{Number(e.hours).toFixed(2)}</td>
                  <td className="px-4 py-3 text-slate-400 max-w-xs truncate">{e.notes ?? '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => { setEditTarget(e); setShowModal(true); }}
                        className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors" title="Edit">
                        <PencilIcon className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => { if (confirm('Delete this entry?')) deleteMutation.mutate(e.id); }}
                        className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded transition-colors" title="Delete">
                        <TrashIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <EntryModal
          entry={editTarget}
          departments={departments}
          onClose={() => { setShowModal(false); setEditTarget(null); }}
        />
      )}
    </div>
  );
}

export default MyTimesheetPage;
